import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { Gem, Megaphone, RotateCcw, UserCog } from "@fleet/icons";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Skeleton,
  Switch,
  TextField,
} from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import {
  AD_NETWORKS,
  AD_TIERS,
  billingQueryKeys,
  clampProviderToPlatform,
  type components,
  isStoreBinaryPlatform,
  PAYMENT_PROVIDER_IDS,
  PURCHASE_PLATFORMS,
  QUOTA_KEYS,
  type QuotaKey,
  resolveAdsConfig,
} from "@listto/shared";
import { useI18n } from "@/providers/i18n-provider";
import { adsEnv } from "@/features/growth/ads-env";
import { useAdminCapabilities } from "../use-admin-capabilities";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ErrorModel = components["schemas"]["ErrorModel"];
type AdminMonetization = components["schemas"]["AdminMonetizationResponse"];

/** `ads.*` keys with their own control in this panel (for the reset button). */
const AD_OVERRIDE_KEYS_UI = [
  "ads.enabled",
  "ads.network",
  "ads.tier",
  "ads.child_directed",
] as const;
type TierDef = components["schemas"]["TierDef"];

/** camelCase field of the QuotaLimits DTO for each snake_case logical key. */
const LIMIT_FIELD: Record<QuotaKey, keyof TierDef["limits"]> = {
  storage_bytes: "storageBytes",
  items: "maxItems",
  collections: "maxCollections",
  orgs: "maxOrgs",
  attachments: "maxAttachments",
};

/**
 * Admin monetization panel (MONETIZATION_QUOTAS, canon).
 *
 * Hot-edits the SAME configuration source as the environment: each change creates a
 * persisted override (`monetization_config`) that supersedes the environment default;
 * deleting the override (Reset) returns control to the environment. The active-overrides
 * active override list makes visible what is overridden and what is not.
 */
export function MonetizationManager() {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();
  const canWrite = () => can("billing.write");

  const query = createApiQuery<AdminMonetization, ErrorModel>(
    () => billingQueryKeys.adminConfig(),
    async () => api.GET("/api/admin/monetization", {}),
    () => ({ staleTime: 5000 }),
  );

  const upsertMutation = createApiMutation(async (vars: { key: string; value: unknown }) =>
    api.POST("/api/admin/monetization", { body: vars }),
  );

  const deleteMutation = createApiMutation(async (vars: { key: string }) =>
    api.DELETE("/api/admin/monetization/{key}", { params: { path: { key: vars.key } } }),
  );

  const subscriptionMutation = createApiMutation(async (vars: { userId: string; tier: string }) =>
    api.POST("/api/admin/monetization/subscription", { body: vars }),
  );

  const resolved = () => query.data?.resolved;
  const overrides = createMemo(() => query.data?.overrides ?? []);
  /**
   * Ads config resolved with the SAME cascade as the client: the bundle
   * env (`VITE_ADS_*`) as default and the `ads.*` rows as override. That way
   * the panel shows exactly what the app will see.
   */
  const adsConfig = createMemo(() =>
    resolveAdsConfig(
      adsEnv(),
      overrides().filter((o) => o.key.startsWith("ads.")),
    ),
  );
  const overriddenKeys = createMemo(() => new Set(overrides().map((o) => o.key)));

  const refetch = () => {
    void query.refetch();
  };

  const saveOverride = (key: string, value: unknown) => {
    upsertMutation.mutate(
      { key, value },
      {
        onSuccess: () => {
          refetch();
          showToast(t(msg`Monetization setting saved`), "success");
        },
        onError: () => {
          showToast(t(msg`Could not save the setting`), "error");
        },
      },
    );
  };

  const resetOverride = (key: string) => {
    deleteMutation.mutate(
      { key },
      {
        onSuccess: () => {
          refetch();
          showToast(t(msg`Setting reset to its environment default`), "success");
        },
      },
    );
  };

  // ── Manual subscription (provider "none") ─────────────────────────────────
  const [subUserId, setSubUserId] = createSignal("");
  const [subTier, setSubTier] = createSignal("");

  const assignSubscription = () => {
    const userId = subUserId().trim();
    const tier = subTier();
    if (!userId || !tier) {
      return;
    }
    subscriptionMutation.mutate(
      { userId, tier },
      {
        onSuccess: () => {
          showToast(t(msg`Subscription assigned`), "success");
          setSubUserId("");
        },
        onError: () => {
          showToast(t(msg`Could not assign the subscription`), "error");
        },
      },
    );
  };

  const quotaLabel = (key: QuotaKey): string => {
    switch (key) {
      case "storage_bytes": {
        return t(msg`Storage (bytes)`);
      }
      case "items": {
        return t(msg`Max items`);
      }
      case "collections": {
        return t(msg`Max collections`);
      }
      case "orgs": {
        return t(msg`Max organizations`);
      }
      default: {
        return t(msg`Max attachments`);
      }
    }
  };

  return (
    <div class="space-y-6" data-testid="monetization-manager">
      <div class="flex items-center gap-3">
        <Gem class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Monetization`)}</h2>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`Tiers, quotas and payment provider. Environment variables are the defaults; every change here overrides them live.`,
            )}
          </p>
        </div>
      </div>

      {/* P23/CC-60 · I3: «no hay respuesta» ≠ «no hay nada». Sin este ramal
          un fallo de red se pintaba como una lista vacía. */}
      <Show when={query.isError}>
        <QueryErrorState
          testId="admin-monetization-error"
          compact
          onRetry={() => void query.refetch()}
        />
      </Show>
      <Show when={!query.isError}>
        <Show
          when={!query.isLoading}
          fallback={
            <div class="space-y-3">
              <Skeleton class="h-24 w-full rounded-xl" />
              <Skeleton class="h-48 w-full rounded-xl" />
            </div>
          }
        >
          {/* Interruptores globales + provider */}
          <Card data-testid="monetization-switches">
            <CardHeader>
              <CardTitle>{t(msg`General`)}</CardTitle>
            </CardHeader>
            <CardContent class="space-y-4">
              <div class="flex items-center justify-between gap-4">
                <div>
                  <Label for="monetization-enabled">{t(msg`Monetization enabled`)}</Label>
                  <p class="text-xs text-muted-foreground">
                    {t(msg`Master switch: quotas, plans screen and ads gating.`)}
                  </p>
                </div>
                <span data-testid="monetization-enabled-switch">
                  <Switch
                    checked={resolved()?.enabled ?? false}
                    disabled={!canWrite() || upsertMutation.isPending}
                    onCheckedChange={(v) => {
                      saveOverride("monetization.enabled", v);
                    }}
                  />
                </span>
              </div>
              <div class="flex items-center justify-between gap-4">
                <div>
                  <Label for="payments-enabled">{t(msg`Payments enabled`)}</Label>
                  <p class="text-xs text-muted-foreground">
                    {t(msg`Offer paid upgrades through the payment provider.`)}
                  </p>
                </div>
                <Switch
                  checked={resolved()?.paymentsEnabled ?? false}
                  disabled={!canWrite() || upsertMutation.isPending}
                  onCheckedChange={(v) => {
                    saveOverride("payments.enabled", v);
                  }}
                />
              </div>
              <div class="flex items-center justify-between gap-4">
                <div>
                  <Label for="payments-provider">{t(msg`Payment provider`)}</Label>
                  <p class="text-xs text-muted-foreground">
                    {t(msg`"none" means upgrades are assigned manually below.`)}
                  </p>
                </div>
                <Select<string>
                  aria-label={t(msg`Payment provider`)}
                  value={resolved()?.provider ?? "none"}
                  options={PAYMENT_PROVIDER_IDS.map((id) => ({ value: id, label: id }))}
                  disabled={!canWrite()}
                  onChange={(v) => {
                    if (v) {
                      saveOverride("payments.provider", v);
                    }
                  }}
                />
              </div>

              {/*
                CLAMP DE PLATAFORMA, hecho visible.

                El Select de arriba configura la pasarela de la WEB. Enseñar
                aquí el proveedor EFECTIVO por superficie es lo que impide el
                error caro: creer que poner "stripe" enciende la compra también
                en el APK. No lo enciende — y no porque la interfaz lo esconda,
                sino porque `clampProviderToPlatform` no deja que ese valor
                exista en un binario de tienda, ni aquí ni en el servidor.
              */}
              <div class="rounded-lg border bg-muted/30 p-3" data-testid="purchase-platform-clamp">
                <p class="text-xs font-medium">{t(msg`Effective provider per platform`)}</p>
                <p class="text-xs text-muted-foreground">
                  {t(
                    msg`Store binaries can never use a web payment gateway: selling inside the app without the store's billing is grounds for removal.`,
                  )}
                </p>
                <div class="mt-2 grid gap-1 sm:grid-cols-2">
                  <For each={PURCHASE_PLATFORMS}>
                    {(platform) => {
                      const effective = () =>
                        clampProviderToPlatform(platform, resolved()?.provider ?? "none");
                      return (
                        <div
                          class="flex items-center justify-between gap-2 text-xs"
                          data-testid={`purchase-platform-${platform}`}
                        >
                          <span class="font-mono">{platform}</span>
                          <Badge
                            variant={isStoreBinaryPlatform(platform) ? "outline" : "secondary"}
                          >
                            {effective()}
                          </Badge>
                        </div>
                      );
                    }}
                  </For>
                </div>
              </div>
            </CardContent>
          </Card>

          {/*
            ADS (kit de growth). Misma fuente que el resto: el env del bundle
            (`VITE_ADS_*`) aporta el default y cada cambio de aquí crea un
            override en `monetization_config` que lo pisa en caliente.
            OFF por defecto ⇒ ningún SDK entra en la app.
          */}
          <Card data-testid="ads-settings">
            <CardHeader>
              <CardTitle class="flex items-center gap-2">
                <Megaphone class="h-5 w-5 text-muted-foreground" />
                {t(msg`Ads`)}
              </CardTitle>
            </CardHeader>
            <CardContent class="space-y-4">
              <div class="flex items-center justify-between gap-4">
                <div>
                  <Label for="ads-enabled">{t(msg`Ads enabled`)}</Label>
                  <p class="text-xs text-muted-foreground">
                    {t(msg`Master switch for the ad layer. Off means no ad SDK is loaded at all.`)}
                  </p>
                </div>
                <span data-testid="ads-enabled-switch">
                  <Switch
                    checked={adsConfig().enabled}
                    disabled={!canWrite() || upsertMutation.isPending}
                    onCheckedChange={(v) => {
                      saveOverride("ads.enabled", v);
                    }}
                  />
                </span>
              </div>
              <div class="flex items-center justify-between gap-4">
                <div>
                  <Label for="ads-network">{t(msg`Ad network`)}</Label>
                  <p class="text-xs text-muted-foreground">
                    {t(msg`"none" is a no-op provider: nothing is requested or rendered.`)}
                  </p>
                </div>
                <Select<string>
                  aria-label={t(msg`Ad network`)}
                  value={adsConfig().network}
                  options={AD_NETWORKS.map((id) => ({ value: id, label: id }))}
                  disabled={!canWrite()}
                  onChange={(v) => {
                    if (v) {
                      saveOverride("ads.network", v);
                    }
                  }}
                />
              </div>
              <div class="flex items-center justify-between gap-4">
                <div>
                  <Label for="ads-tier">{t(msg`Ad tier`)}</Label>
                  <p class="text-xs text-muted-foreground">
                    {t(msg`C (B2B) and D (privacy) force zero ads regardless of the switch.`)}
                  </p>
                </div>
                <Select<string>
                  aria-label={t(msg`Ad tier`)}
                  value={adsConfig().tier}
                  options={AD_TIERS.map((id) => ({ value: id, label: id }))}
                  disabled={!canWrite()}
                  onChange={(v) => {
                    if (v) {
                      saveOverride("ads.tier", v);
                    }
                  }}
                />
              </div>
              <div class="flex items-center justify-between gap-4">
                <div>
                  <Label for="ads-child">{t(msg`Child-directed treatment`)}</Label>
                  <p class="text-xs text-muted-foreground">
                    {t(msg`COPPA/Families regime: G rating, no behavioural targeting.`)}
                  </p>
                </div>
                <Switch
                  checked={adsConfig().childDirected}
                  disabled={!canWrite() || upsertMutation.isPending}
                  onCheckedChange={(v) => {
                    saveOverride("ads.child_directed", v);
                  }}
                />
              </div>
              <For each={AD_OVERRIDE_KEYS_UI}>
                {(key) => (
                  <Show when={overriddenKeys().has(key)}>
                    <button
                      type="button"
                      class="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                      disabled={!canWrite()}
                      onClick={() => {
                        resetOverride(key);
                      }}
                    >
                      <RotateCcw class="h-3 w-3" />
                      {t(msg`Reset ${key} to env`)}
                    </button>
                  </Show>
                )}
              </For>
            </CardContent>
          </Card>

          {/* Tiers y límites */}
          <For each={resolved()?.tiers ?? []}>
            {(tier) => (
              <Card data-testid={`monetization-tier-${tier.id}`}>
                <CardHeader>
                  <CardTitle class="flex items-center justify-between gap-3">
                    <span>
                      {tier.label}{" "}
                      <span class="text-muted-foreground font-mono text-sm">({tier.id})</span>
                    </span>
                    <div class="flex items-center gap-2">
                      <span class="text-xs text-muted-foreground">{t(msg`Ads`)}</span>
                      <Switch
                        checked={tier.adsEnabled}
                        disabled={!canWrite() || upsertMutation.isPending}
                        onCheckedChange={(v) => {
                          saveOverride(`tier.${tier.id}.ads_enabled`, v);
                        }}
                      />
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent class="grid gap-3 sm:grid-cols-2">
                  <For each={QUOTA_KEYS}>
                    {(key) => {
                      const overrideKey = `tier.${tier.id}.${key}`;
                      const current = () => tier.limits[LIMIT_FIELD[key]] ?? null;
                      return (
                        <div class="space-y-1">
                          <div class="flex items-center justify-between">
                            <Label for={`limit-${tier.id}-${key}`} class="text-xs">
                              {quotaLabel(key)}
                            </Label>
                            <Show when={overriddenKeys().has(overrideKey)}>
                              <button
                                type="button"
                                class="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                                disabled={!canWrite()}
                                onClick={() => {
                                  resetOverride(overrideKey);
                                }}
                                data-testid={`reset-${tier.id}-${key}`}
                              >
                                <RotateCcw class="h-3 w-3" />
                                {t(msg`Reset to env`)}
                              </button>
                            </Show>
                          </div>
                          <TextField
                            id={`limit-${tier.id}-${key}`}
                            type="number"
                            placeholder={t(msg`-1 = unlimited`)}
                            value={current() === null ? "" : String(current())}
                            disabled={!canWrite()}
                            data-testid={`limit-${tier.id}-${key}`}
                            onChange={(e) => {
                              const raw = e.currentTarget.value.trim();
                              const parsed = raw === "" ? -1 : Number.parseInt(raw, 10);
                              if (Number.isNaN(parsed)) {
                                return;
                              }
                              if (parsed === (current() ?? -1)) {
                                return;
                              }
                              saveOverride(overrideKey, parsed);
                            }}
                          />
                        </div>
                      );
                    }}
                  </For>
                </CardContent>
              </Card>
            )}
          </For>

          {/* Overrides activos (qué pisa al env ahora mismo) */}
          <Card data-testid="monetization-overrides">
            <CardHeader>
              <CardTitle>{t(msg`Active overrides`)}</CardTitle>
            </CardHeader>
            <CardContent>
              <Show
                when={overrides().length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground">
                    {t(msg`No overrides. All values come from the environment defaults.`)}
                  </p>
                }
              >
                <div class="space-y-2">
                  <For each={overrides()}>
                    {(row) => (
                      <div class="flex items-center justify-between gap-3 text-sm">
                        <div class="min-w-0 flex items-center gap-2">
                          <span class="font-mono truncate">{row.key}</span>
                          <Badge variant="secondary">{JSON.stringify(row.value)}</Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!canWrite() || deleteMutation.isPending}
                          onClick={() => {
                            resetOverride(row.key);
                          }}
                        >
                          {t(msg`Remove`)}
                        </Button>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
            </CardContent>
          </Card>

          {/* Suscripción manual (flujo del provider "none") */}
          <Card data-testid="monetization-manual-subscription">
            <CardHeader>
              <CardTitle class="flex items-center gap-2">
                <UserCog class="h-5 w-5" />
                {t(msg`Assign subscription manually`)}
              </CardTitle>
            </CardHeader>
            <CardContent class="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div class="flex-1 space-y-1">
                <Label for="sub-user-id">{t(msg`User id`)}</Label>
                <TextField
                  id="sub-user-id"
                  value={subUserId()}
                  disabled={!canWrite()}
                  onInput={(e) => {
                    setSubUserId(e.currentTarget.value);
                  }}
                />
              </div>
              <div class="space-y-1">
                <Label for="sub-tier">{t(msg`Tier`)}</Label>
                <Select<string>
                  aria-label={t(msg`Tier`)}
                  placeholder={t(msg`Pick a tier`)}
                  value={subTier()}
                  options={(resolved()?.tiers ?? []).map((tier) => ({
                    value: tier.id,
                    label: tier.label,
                  }))}
                  disabled={!canWrite()}
                  onChange={setSubTier}
                />
              </div>
              <Button
                disabled={
                  !canWrite() || subscriptionMutation.isPending || !subUserId().trim() || !subTier()
                }
                onClick={assignSubscription}
              >
                {t(msg`Assign`)}
              </Button>
            </CardContent>
          </Card>
        </Show>
      </Show>
    </div>
  );
}
